import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { ChangeSet, Compartment, EditorSelection, EditorState, MapMode, Transaction, type Text } from '@codemirror/state';
import {
  EditorView,
  drawSelection,
  highlightActiveLine,
  highlightActiveLineGutter,
  highlightSpecialChars,
  keymap,
  lineNumbers,
  placeholder as cmPlaceholder,
} from '@codemirror/view';
import type { Severity } from '../engine/types';
import type { RowTone } from '../lib/rowStatus';
import {
  diagnosticsExtension,
  setLineDiagnostics,
  setLineStatuses,
  type LineDiagnosticItem,
  type LineDiagnostics,
  type LineStatus,
} from './diagnostics';
import { patternLanguage } from './language';
import { editorTheme } from './theme';

export interface LineDiagnosticsInput {
  /** 1-based line number in the text that was compiled. */
  line_no: number;
  severity: Severity;
  items: LineDiagnosticItem[];
}

/** A row and its reading tone for the selected size, with the localized reason ('' when there is nothing to say). */
export interface LineStatusInput {
  /** 1-based line number in the text that was compiled. */
  line_no: number;
  tone: RowTone;
  label: string;
}

export interface PatternEditorHandle {
  getText(): string;
  /** Replaces the whole document (undoable unless `history: false`); the cursor goes to its start, or its end. */
  replaceAll(text: string, opts?: { history?: boolean; cursor?: 'start' | 'end' }): void;
  /** Marks the current document as "being compiled"; later edits are tracked against it. */
  beginSnapshot(): { id: number; text: string };
  /** Shows diagnostics computed for snapshot `id`, which becomes the reference for line mapping. */
  commitDiagnostics(id: number, lines: LineDiagnosticsInput[]): void;
  /** Marks the rows of snapshot `id` with their reading tone (after `commitDiagnostics` for that snapshot). */
  commitRowStatuses(id: number, lines: LineStatusInput[]): void;
  /** Current line number for a line number of the reference snapshot (null if that line was deleted). */
  currentLineFor(resultLineNo: number): number | null;
  /** Scrolls to, selects and focuses a line given in reference-snapshot coordinates. */
  revealLine(resultLineNo: number): void;
  replaceRange(from: number, to: number, insert: string): void;
  focus(): void;
}

interface Props {
  onChange: (info: { empty: boolean }) => void;
  onCursorLine?: (line: number) => void;
  onCompileNow: () => void;
  /** Text the user just pasted (for the "pasted from a PDF" offer). */
  onPaste?: (text: string) => void;
  placeholder: string;
  ariaLabel: string;
  /** Line numbers are opt-in (the "Affichage" menu). */
  lineNumbers?: boolean;
  className?: string;
}

interface Snapshot {
  id: number;
  doc: Text;
  changes: ChangeSet;
}

export const PatternEditor = forwardRef<PatternEditorHandle, Props>(function PatternEditor(
  { onChange, onCursorLine, onCompileNow, onPaste, placeholder, ariaLabel, lineNumbers: showLineNumbers = false, className },
  ref,
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const labelsCompartment = useRef(new Compartment());
  const gutterCompartment = useRef(new Compartment());
  const initialLineNumbers = useRef(showLineNumbers);
  const snapshots = useRef<Snapshot[]>([]);
  const reference = useRef<Snapshot | null>(null);
  const nextId = useRef(1);
  const callbacks = useRef({ onChange, onCursorLine, onCompileNow, onPaste });
  callbacks.current = { onChange, onCursorLine, onCompileNow, onPaste };
  const initialLabels = useRef({ placeholder, ariaLabel });

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let lastLine = -1;
    const view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: '',
        extensions: [
          gutterCompartment.current.of(initialLineNumbers.current ? [lineNumbers(), highlightActiveLineGutter()] : []),
          highlightSpecialChars(),
          history(),
          drawSelection(),
          highlightActiveLine(),
          EditorView.lineWrapping,
          patternLanguage(),
          diagnosticsExtension(),
          editorTheme,
          keymap.of([
            {
              key: 'Mod-Enter',
              preventDefault: true,
              run: () => {
                callbacks.current.onCompileNow();
                return true;
              },
            },
            ...defaultKeymap,
            ...historyKeymap,
          ]),
          labelsCompartment.current.of(labelExtensions(initialLabels.current.placeholder, initialLabels.current.ariaLabel)),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              for (const s of snapshots.current) s.changes = s.changes.compose(update.changes);
              callbacks.current.onChange({ empty: update.state.doc.length === 0 });
              for (const tr of update.transactions) {
                if (!tr.isUserEvent('input.paste')) continue;
                let pasted = '';
                tr.changes.iterChanges((_fa, _ta, _fb, _tb, inserted) => {
                  pasted += inserted.toString();
                });
                if (pasted) callbacks.current.onPaste?.(pasted);
              }
            }
            if (update.selectionSet || update.docChanged) {
              const line = update.state.doc.lineAt(update.state.selection.main.head).number;
              if (line !== lastLine) {
                lastLine = line;
                callbacks.current.onCursorLine?.(line);
              }
            }
          }),
        ],
      }),
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []);

  useEffect(() => {
    viewRef.current?.dispatch({ effects: labelsCompartment.current.reconfigure(labelExtensions(placeholder, ariaLabel)) });
  }, [placeholder, ariaLabel]);

  useEffect(() => {
    viewRef.current?.dispatch({
      effects: gutterCompartment.current.reconfigure(showLineNumbers ? [lineNumbers(), highlightActiveLineGutter()] : []),
    });
  }, [showLineNumbers]);

  useImperativeHandle(ref, () => {
    const view = () => viewRef.current;
    const currentLineFor = (resultLineNo: number): number | null => {
      const v = view();
      if (!v) return null;
      const snap = reference.current;
      if (!snap) return resultLineNo >= 1 && resultLineNo <= v.state.doc.lines ? resultLineNo : null;
      if (resultLineNo < 1 || resultLineNo > snap.doc.lines) return null;
      const pos = snap.changes.mapPos(snap.doc.line(resultLineNo).from, -1, MapMode.TrackDel);
      if (pos === null) return null;
      return v.state.doc.lineAt(Math.min(pos, v.state.doc.length)).number;
    };
    return {
      getText: () => view()?.state.doc.toString() ?? '',
      replaceAll(text, opts) {
        const v = view();
        if (!v) return;
        v.dispatch({
          changes: { from: 0, to: v.state.doc.length, insert: text },
          selection: { anchor: opts?.cursor === 'end' ? v.state.toText(text).length : 0 },
          scrollIntoView: true,
          annotations: opts?.history === false ? Transaction.addToHistory.of(false) : [],
        });
      },
      beginSnapshot() {
        const v = view();
        const doc = v ? v.state.doc : (EditorState.create().doc as Text);
        const snap: Snapshot = { id: nextId.current++, doc, changes: ChangeSet.empty(doc.length) };
        snapshots.current = [...snapshots.current.slice(-6), snap];
        return { id: snap.id, text: doc.toString() };
      },
      commitDiagnostics(id, lines) {
        const v = view();
        const snap = snapshots.current.find((s) => s.id === id) ?? (reference.current?.id === id ? reference.current : null);
        if (!v || !snap) return;
        reference.current = snap;
        snapshots.current = snapshots.current.filter((s) => s.id >= id);
        const entries: LineDiagnostics[] = [];
        for (const l of lines) {
          const from = lineStart(v, snap, l.line_no);
          if (from !== null) entries.push({ from, severity: l.severity, items: l.items });
        }
        v.dispatch({ effects: setLineDiagnostics.of(entries) });
      },
      commitRowStatuses(id, lines) {
        const v = view();
        const snap = reference.current?.id === id ? reference.current : (snapshots.current.find((s) => s.id === id) ?? null);
        if (!v || !snap) return;
        const entries: LineStatus[] = [];
        for (const l of lines) {
          const from = lineStart(v, snap, l.line_no);
          if (from !== null) entries.push({ from, tone: l.tone, label: l.label });
        }
        v.dispatch({ effects: setLineStatuses.of(entries) });
      },
      currentLineFor,
      revealLine(resultLineNo) {
        const v = view();
        if (!v) return;
        const n = currentLineFor(resultLineNo);
        if (n === null) return;
        const line = v.state.doc.line(n);
        v.dispatch({
          selection: EditorSelection.range(line.from, line.to),
          effects: EditorView.scrollIntoView(line.from, { y: 'center' }),
        });
        v.focus();
      },
      replaceRange(from, to, insert) {
        const v = view();
        if (!v) return;
        v.dispatch({
          changes: { from, to, insert },
          selection: EditorSelection.range(from, from + insert.length),
          scrollIntoView: true,
          userEvent: 'input.fix',
        });
      },
      focus: () => view()?.focus(),
    };
  }, []);

  return <div ref={hostRef} className={className} data-testid="editor" />;
});

/** Start of the current line that line `resultLineNo` of snapshot `snap` became (null if it was deleted). */
function lineStart(view: EditorView, snap: Snapshot, resultLineNo: number): number | null {
  if (resultLineNo < 1 || resultLineNo > snap.doc.lines) return null;
  const pos = snap.changes.mapPos(snap.doc.line(resultLineNo).from, -1, MapMode.TrackDel);
  if (pos === null) return null;
  return view.state.doc.lineAt(Math.min(pos, view.state.doc.length)).from;
}

function labelExtensions(placeholder: string, ariaLabel: string) {
  return [cmPlaceholder(placeholder), EditorView.contentAttributes.of({ 'aria-label': ariaLabel, spellcheck: 'false' })];
}
